/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationKeyframe} from '../../src/animation/animation_keyframe';
import {AnimationPlayer} from '../../src/animation/animation_player';
import {AnimationStyles} from '../../src/animation/animation_styles';
import {Injector} from '../di/injector';
import {unimplemented} from '../facade/errors';
import {ViewEncapsulation} from '../metadata/view';

/**
 * @experimental
 */
// TODO (matsko): add typing for the animation function
export class RenderComponentType {
/**
 * @param {?} id
 * @param {?} templateUrl
 * @param {?} slotCount
 * @param {?} encapsulation
 * @param {?} styles
 * @param {?} animations
 */
constructor(
public id: string,
public templateUrl: string,
public slotCount: number,
public encapsulation: ViewEncapsulation,
public styles: Array<string|any[]>,
public animations: {[key: string]: Function}) {}
}

function RenderComponentType_tsickle_Closure_declarations() {
 /** @type {?} */
RenderComponentType.prototype.id;
 /** @type {?} */
RenderComponentType.prototype.templateUrl;
 /** @type {?} */
RenderComponentType.prototype.slotCount;
 /** @type {?} */
RenderComponentType.prototype.encapsulation;
 /** @type {?} */
RenderComponentType.prototype.styles;
 /** @type {?} */
RenderComponentType.prototype.animations;
}

/**
 * @abstract
 */
export abstract class RenderDebugInfo {
/**
 * @return {?}
 */
get injector(): Injector { return unimplemented(); }
/**
 * @return {?}
 */
get component(): any { return unimplemented(); }
/**
 * @return {?}
 */
get providerTokens(): any[] { return unimplemented(); }
/**
 * @return {?}
 */
get references(): {[key: string]: any} { return unimplemented(); }
/**
 * @return {?}
 */
get context(): any { return unimplemented(); }
/**
 * @return {?}
 */
get source(): string { return unimplemented(); }
}

export interface DirectRenderer {
  remove(node: any): void;
  appendChild(node: any, parent: any): void;
  insertBefore(node: any, refNode: any): void;
  nextSibling(node: any): any;
  parentElement(node: any): any;
}
/**
 * @abstract
 */
export abstract class Renderer {
/**
 * @abstract
 * @param {?} selectorOrNode
 * @param {?=} debugInfo
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} name
 * @param {?=} debugInfo
 * @return {?}
 */
createElement(parentElement: any, name: string, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} hostElement
 * @return {?}
 */
createViewRoot(hostElement: any) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?=} debugInfo
 * @return {?}
 */
createTemplateAnchor(parentElement: any, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} value
 * @param {?=} debugInfo
 * @return {?}
 */
createText(parentElement: any, value: string, debugInfo?: RenderDebugInfo) {}
/**
 * @abstract
 * @param {?} parentElement
 * @param {?} nodes
 * @return {?}
 */
projectNodes(parentElement: any, nodes: any[]) {}
/**
 * @abstract
 * @param {?} node
 * @param {?} viewRootNodes
 * @return {?}
 */
attachViewAfter(node: any, viewRootNodes: any[]) {}
/**
 * @abstract
 * @param {?} viewRootNodes
 * @return {?}
 */
detachView(viewRootNodes: any[]) {}
/**
 * @abstract
 * @param {?} hostElement
 * @param {?} viewAllNodes
 * @return {?}
 */
destroyView(hostElement: any, viewAllNodes: any[]) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listen(renderElement: any, name: string, callback: Function) {}
/**
 * @abstract
 * @param {?} target
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listenGlobal(target: string, name: string, callback: Function) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setElementProperty(renderElement: any, propertyName: string, propertyValue: any) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} attributeName
 * @param {?} attributeValue
 * @return {?}
 */
setElementAttribute(renderElement: any, attributeName: string, attributeValue: string) {}
/**
 *  Used only in debug mode to serialize property changes to dom nodes as attributes.
 * @abstract
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setBindingDebugInfo(renderElement: any, propertyName: string, propertyValue: string) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} className
 * @param {?} isAdd
 * @return {?}
 */
setElementClass(renderElement: any, className: string, isAdd: boolean) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setElementStyle(renderElement: any, styleName: string, styleValue: string) {}
/**
 * @abstract
 * @param {?} renderElement
 * @param {?} methodName
 * @param {?=} args
 * @return {?}
 */
invokeElementMethod(renderElement: any, methodName: string, args?: any[]) {}
/**
 * @abstract
 * @param {?} renderNode
 * @param {?} text
 * @return {?}
 */
setText(renderNode: any, text: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} startingStyles
 * @param {?} keyframes
 * @param {?} duration
 * @param {?} delay
 * @param {?} easing
 * @param {?=} previousPlayers
 * @return {?}
 */
animate(
      element: any, startingStyles: AnimationStyles, keyframes: AnimationKeyframe[],
      duration: number, delay: number, easing: string,
      previousPlayers?: AnimationPlayer[]) {}
}
/**
 *  Injectable service that provides a low-level interface for modifying the UI.
  * *
  * Use this service to bypass Angular's templating and make custom UI changes that can't be
  * expressed declaratively. For example if you need to set a property or an attribute whose name is
  * not statically known, use {@link #setElementProperty} or {@link #setElementAttribute}
  * respectively.
  * *
  * If you are implementing a custom renderer, you must implement this interface.
  * *
  * The default Renderer implementation is `DomRenderer`. Also available is `WebWorkerRenderer`.
 * @abstract
 */
export abstract class RootRenderer {
/**
 * @abstract
 * @param {?} componentType
 * @return {?}
 */
renderComponent(componentType: RenderComponentType) {}
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
